import { Component, computed, effect, input, model, signal, WritableSignal } from '@angular/core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PercentPipe } from '@angular/common';
import { Observable } from 'rxjs';
import { AbstractCoreComponent } from '@shared/abstracts/abstract-core.component';

@Component({
  selector: 'app-trend',
  imports: [
    TagModule,
    TooltipModule,
    SkeletonModule,
    FontAwesomeModule,
    PercentPipe,
  ],
  providers: [],
  template: `
    @if (hasVisibleContent()) {
      <div [id]="uuid" class="line-clamp-2">
        @if (delta() === 'relative' && showRelativeTrend()) {
          <p-tag [severity]="severity()" class="mr-1" value="{{ sign() }}{{ relative() | percent }}"></p-tag>
          @if (days()) { <small class="font-medium">{{ label() }}</small> }
        } @else if (delta() === 'absolute') {
          <p-tag [severity]="severity()" class="mr-1" value="{{ sign() }}{{ absolute() }}"></p-tag>
          @if (suffix()) { <small class="text-muted-color">{{ suffix() }}</small> }
        } @else {
          <span class="font-medium"></span>
          <small class="text-muted-color" i18n>Analisi del trend non disponibile</small>
        }
      </div>
    }
  `
})
export class TrendComponent extends AbstractCoreComponent {
  /**
   * Represents the current model that can hold either a number or an Observable emitting number.
   */
  current = input<number | Observable<number>>();

  /**
   * Represents the numeric value displayed that may either be a number or undefined.
   */
  protected currentValue = signal<number | undefined>(undefined);

  /**
   * Represents the previous model that can hold either a number or an Observable emitting number.
   */
  previous = model<number | Observable<number>>();

  /**
   * Represents the numeric value displayed that may either be a number or undefined.
   */
  protected previousValue = signal<number | undefined>(undefined);

  /**
   * Represents the number of days.
   *
   * This variable holds a numeric value that specifies a count of days.
   * It should be assigned a valid number to represent the intended duration or quantity of days.
   */
  days = input<number>();

  /**
   * Represents the mode of change or difference being applied.
   *
   * The `delta` variable can take one of two string values:
   * - 'relative': Indicates that the change is relative to a previous value.
   * - 'absolute': Indicates that the change is an absolute value.
   *
   * This variable is often used in scenarios where it is important to specify
   * whether computations or transformations should be applied in a relative or
   * absolute context.
   */
  delta = input<'relative' | 'absolute'>();

  /**
   * Represents a boolean input variable named 'higher' used to define the multiplier
   * used to elaborate the trend severity.
   */
  higher = input<boolean>(true);

  /**
   * Represents a string variable named 'suffix' used to define a suffix to be appended.
   */
  suffix = input<string>();

  /**
   * Represents a computed variable named 'label' that returns a string value based on the
   * days set. This variable is used to provide a label or description for the trend in case
   * the delta is equal to 'relative'.
   * If days are greater than 1, the label will be plural, otherwise it will be singular.
   */
  label = computed(() => {
    const days = this.days();
    if (!days) return '';
    return days > 1 ? $localize`rispetto ai ${days} giorni precedenti` : $localize`rispetto al giorno precedente`;
  });

  /**
   * Represents a computed variable named 'sign' that returns a string value that represents
   * the sign of the change or difference. This variable is used to determine the direction of the trend.
   */
  sign = computed(() => {
    const trendValue = this._getTrendValue();
    return trendValue !== null && trendValue > 0 ? '+' : '';
  });

  /**
   * Represents a computed variable named 'severity' that returns a string value that represents
   * the severity of the trend. This variable is used to determine the color of the trend.
   */
  severity = computed(() => {
    const trendValue = this._getTrendValue();
    if (trendValue === null) return 'info';
    const adjustedValue = trendValue * (this.higher() ? 1 : -1);
    if (adjustedValue > 0) return 'success';
    if (adjustedValue === 0) return 'info';
    return 'danger';
  });

  /**
   * Represents a computed variable named 'absolute' that returns a numeric value that represents
   * the absolute change or difference. This variable is used to calculate the absolute change.
   */
  absolute = computed(() => {
    let value = null;
    const current = this.currentValue();
    const previous = this.previousValue();
    if (current !== undefined && previous !== undefined) {
      value = current - previous;
    }
    this.logger.debug(this.name, 'Trend absolute value', value);
    return value;
  });

  /**
   * Represents a computed variable named 'relative' that returns a numeric value that represents
   * the relative change or difference. This variable is used to calculate the relative change.
   */
  relative = computed(() => {
    let value = null;
    const current = this.currentValue();
    const previous = this.previousValue();
    if (current !== undefined && previous !== undefined) {
      if (previous === 0) return null;
      value = ((current - previous) / previous ); // angular percent pipe used
    }
    this.logger.debug(this.name, 'Trend relative value', value);
    return value;
  });

  /** Hide comparison label and tag when KPI data is missing or not comparable. */
  showRelativeTrend = computed(() => {
    const current = this.currentValue();
    const previous = this.previousValue();
    if (current == null || previous == null) {
      return false;
    }
    if (current === 0 && previous === 0) {
      return false;
    }
    return this.relative() != null;
  });

  hasVisibleContent = computed(() => {
    const mode = this.delta();
    if (mode === 'relative') {
      return this.showRelativeTrend();
    }
    if (mode === 'absolute') {
      return this.absolute() != null;
    }
    return mode != null;
  });

  constructor() {
    super();
    effect((onCleanup) => {
      onCleanup(this._bindInput(this.current(), this.currentValue));
    });
    effect((onCleanup) => {
      onCleanup(this._bindInput(this.previous(), this.previousValue));
    });
  }

  private _getTrendValue(): number | null {
    return this.delta() === 'relative' ? this.relative() : this.absolute();
  }

  private _bindInput(
    input: number | Observable<number> | undefined,
    target: WritableSignal<number | undefined>,
  ): () => void {
    if (input instanceof Observable) {
      const subscription = input.subscribe((value: number) => target.set(value));
      return () => subscription.unsubscribe();
    }
    if (typeof input === 'number') {
      target.set(input);
    } else {
      target.set(undefined);
    }
    return () => undefined;
  }
}
